import { EdgeLabelRenderer, EdgeProps, useReactFlow, Position } from 'reactflow';
import { X, Plus, ArrowRight } from 'lucide-react';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useState, useRef, useEffect, useMemo } from 'react';
import { getNodeDefinition } from '@/lib/node-registry';
import { workflowColors } from '@/lib/workflow-colors';

// Extended EdgeProps to include sourceHandle and targetHandle
interface ExtendedEdgeProps extends EdgeProps {
  sourceHandle?: string | null;
  targetHandle?: string | null;
}

// Configuration
const GRID_SIZE = 20;
const NODE_PADDING = 40;
const EXIT_DISTANCE = 60;
const MAX_ITERATIONS = 500;
const CORNER_RADIUS = 12;
const CLEARANCE_OFFSET = 40; // For rectangular routing above/below nodes

interface Point {
  x: number;
  y: number;
}

interface Obstacle {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface GridNode {
  x: number;
  y: number;
  g: number;
  h: number;
  f: number;
  parent?: GridNode;
}

function heuristic(a: Point, b: Point): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

function isColliding(x: number, y: number, obstacles: Obstacle[]): boolean {
  return obstacles.some(obs => {
    return (
      x >= obs.x - NODE_PADDING &&
      x <= obs.x + obs.width + NODE_PADDING &&
      y >= obs.y - NODE_PADDING &&
      y <= obs.y + obs.height + NODE_PADDING
    );
  });
}

/**
 * Get exit waypoint based on handle position
 */
function getExitWaypoint(
  point: Point,
  position: Position,
  distance: number = EXIT_DISTANCE
): Point {
  switch(position) {
    case Position.Left:
      return { x: point.x - distance, y: point.y };
    case Position.Right:
      return { x: point.x + distance, y: point.y };
    case Position.Top:
      return { x: point.x, y: point.y - distance };
    case Position.Bottom:
      return { x: point.x, y: point.y + distance };
    default:
      return point;
  }
}

/**
 * Create rectangular routing for backward flow (loop edges)
 * Creates a 6-point path that routes above or below nodes
 */
function createRectangularPath(
  sourceExit: Point,
  targetEntry: Point,
  obstacles: Obstacle[],
  shouldRouteAbove: boolean
): Point[] {
  // Route above or below based on the handle's position on the node
  // If the handle is in the upper half of the node, route above
  // If the handle is in the lower half of the node, route below
  
  // Find the highest/lowest point among obstacles to determine clearance
  let extremeY = shouldRouteAbove ? Infinity : -Infinity;
  
  for (const obs of obstacles) {
    if (shouldRouteAbove) {
      extremeY = Math.min(extremeY, obs.y - CLEARANCE_OFFSET);
    } else {
      extremeY = Math.max(extremeY, obs.y + obs.height + CLEARANCE_OFFSET);
    }
  }
  
  // If no obstacles, use source/target Y with offset
  if (extremeY === Infinity || extremeY === -Infinity) {
    extremeY = shouldRouteAbove 
      ? Math.min(sourceExit.y, targetEntry.y) - 100
      : Math.max(sourceExit.y, targetEntry.y) + 100;
  }
  
  // Create 6-point rectangular path:
  // 1. sourceExit (already have)
  // 2. Horizontal extension from source
  // 3. Vertical to clearance level
  // 4. Horizontal across to target X
  // 5. Vertical down to target Y level
  // 6. targetEntry (already have)
  
  const horizontalExtension = sourceExit.x + EXIT_DISTANCE;
  
  return [
    sourceExit,
    { x: horizontalExtension, y: sourceExit.y },
    { x: horizontalExtension, y: extremeY },
    { x: targetEntry.x - EXIT_DISTANCE, y: extremeY },
    { x: targetEntry.x - EXIT_DISTANCE, y: targetEntry.y },
    targetEntry,
  ];
}

/**
 * A* pathfinding algorithm
 */
function findPath(
  start: Point,
  end: Point,
  obstacles: Obstacle[]
): Point[] {
  const openSet: GridNode[] = [];
  const closedSet = new Set<string>();
  
  const startNode: GridNode = {
    x: Math.round(start.x / GRID_SIZE) * GRID_SIZE,
    y: Math.round(start.y / GRID_SIZE) * GRID_SIZE,
    g: 0,
    h: heuristic(start, end),
    f: heuristic(start, end),
  };
  
  openSet.push(startNode);
  
  const endPoint = {
    x: Math.round(end.x / GRID_SIZE) * GRID_SIZE,
    y: Math.round(end.y / GRID_SIZE) * GRID_SIZE,
  };
  
  let iterations = 0;
  
  while (openSet.length > 0 && iterations < MAX_ITERATIONS) {
    iterations++;
    
    openSet.sort((a, b) => a.f - b.f);
    const current = openSet.shift()!;
    const currentKey = `${current.x},${current.y}`;
    
    if (
      Math.abs(current.x - endPoint.x) < GRID_SIZE * 2 && 
      Math.abs(current.y - endPoint.y) < GRID_SIZE * 2
    ) {
      const path: Point[] = [];
      let node: GridNode | undefined = current;
      while (node) {
        path.unshift({ x: node.x, y: node.y });
        node = node.parent;
      }
      path.push(end);
      return simplifyPath(path);
    }
    
    closedSet.add(currentKey);
    
    const neighbors = [
      { x: current.x + GRID_SIZE, y: current.y },
      { x: current.x - GRID_SIZE, y: current.y },
      { x: current.x, y: current.y + GRID_SIZE },
      { x: current.x, y: current.y - GRID_SIZE },
    ];
    
    for (const neighbor of neighbors) {
      const neighborKey = `${neighbor.x},${neighbor.y}`;
      
      if (closedSet.has(neighborKey)) continue;
      if (isColliding(neighbor.x, neighbor.y, obstacles)) continue;
      
      const g = current.g + GRID_SIZE;
      const h = heuristic(neighbor, endPoint);
      const f = g + h;
      
      const existingNode = openSet.find(
        n => n.x === neighbor.x && n.y === neighbor.y
      );
      
      if (!existingNode) {
        openSet.push({
          x: neighbor.x,
          y: neighbor.y,
          g,
          h,
          f,
          parent: current,
        });
      } else if (g < existingNode.g) {
        existingNode.g = g;
        existingNode.f = f;
        existingNode.parent = current;
      }
    }
  }
  
  return [start, end];
}

function simplifyPath(path: Point[]): Point[] {
  if (path.length <= 2) return path;
  
  const simplified: Point[] = [path[0]];
  
  for (let i = 1; i < path.length - 1; i++) {
    const prev = path[i - 1];
    const current = path[i];
    const next = path[i + 1];
    
    const dir1 = { x: current.x - prev.x, y: current.y - prev.y };
    const dir2 = { x: next.x - current.x, y: next.y - current.y };
    
    if (dir1.x !== dir2.x || dir1.y !== dir2.y) {
      simplified.push(current);
    }
  }
  
  simplified.push(path[path.length - 1]);
  return simplified;
}

function createSmoothPath(pathPoints: Point[]): string {
  if (pathPoints.length < 2) return '';
  
  let pathData = `M ${pathPoints[0].x} ${pathPoints[0].y}`;
  
  for (let i = 1; i < pathPoints.length; i++) {
    const prev = pathPoints[i - 1];
    const current = pathPoints[i];
    
    if (i < pathPoints.length - 1) {
      const next = pathPoints[i + 1];
      
      const dx1 = current.x - prev.x;
      const dy1 = current.y - prev.y;
      const dx2 = next.x - current.x;
      const dy2 = next.y - current.y;
      
      const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
      const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
      
      if (len1 > 0 && len2 > 0) {
        const offset1 = Math.min(CORNER_RADIUS, len1 / 2);
        const offset2 = Math.min(CORNER_RADIUS, len2 / 2);
        
        const p1x = current.x - (dx1 / len1) * offset1;
        const p1y = current.y - (dy1 / len1) * offset1;
        const p2x = current.x + (dx2 / len2) * offset2;
        const p2y = current.y + (dy2 / len2) * offset2;
        
        pathData += ` L ${p1x} ${p1y} Q ${current.x} ${current.y} ${p2x} ${p2y}`;
      } else {
        pathData += ` L ${current.x} ${current.y}`;
      }
    } else {
      pathData += ` L ${current.x} ${current.y}`;
    }
  }
  
  return pathData;
}

export function SmartEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  source,
  target,
  sourceHandle = null,
  targetHandle = null,
  style = {},
  markerEnd,
  data,
  selected,
}: ExtendedEdgeProps) {
  const { getNodes, getEdges } = useReactFlow();
  
  // Get the actual edge object from React Flow to access sourceHandle/targetHandle
  const edges = getEdges();
  const edgeData = edges.find(e => e.id === id);
  const actualSourceHandle = edgeData?.sourceHandle || sourceHandle;
  const actualTargetHandle = edgeData?.targetHandle || targetHandle;
  
  const { removeEdge, selectEdge, selectedEdge, highlightedEdgeId } = useWorkflowStore();
  const isDropTarget = highlightedEdgeId === id;
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSelected = selected || selectedEdge?.id === id;
  
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);
  
  // ✅ FIX: Include ALL nodes as obstacles (don't exclude source/target)
  const obstacles = useMemo(() => {
    const nodes = getNodes();
    return nodes.map(node => {
      const isComplexNode = 
        node.data?.category === 'trigger' || 
        node.data?.category === 'flow-control';
      const defaultWidth = isComplexNode ? 128 : 96;
      const defaultHeight = isComplexNode ? 128 : 96;
      
      return {
        x: node.position.x,
        y: node.position.y,
        width: node.width || (node as any).measured?.width || defaultWidth,
        height: node.height || (node as any).measured?.height || defaultHeight,
      };
    });
  }, [getNodes]);
  
  // Calculate path with exit waypoints
  const pathPoints = useMemo(() => {
    // Check if this is a sub-node connection (from AI Agent anchors or other sub-node anchors)
    const isSubNodeConnection = actualSourceHandle && (
      actualSourceHandle === 'chatModel' ||
      actualSourceHandle === 'memory' ||
      actualSourceHandle === 'tools' ||
      actualSourceHandle === 'parser' ||
      actualSourceHandle === 'pollSource'
    );
    
    // Debug logging (commented out to reduce console noise)
    // console.log('SmartEdge - actualSourceHandle:', actualSourceHandle, 'isSubNodeConnection:', isSubNodeConnection);
    
    // For sub-node connections, use simple path that always connects to target handle
    // This creates a clean path from AI Agent anchor to sub-node
    if (isSubNodeConnection) {
      const horizontalDistance = Math.abs(targetX - sourceX);
      
      // If nodes are perfectly aligned (within 5px), draw straight vertical line
      if (horizontalDistance < 5) {
        // Pure vertical line - straight down to target
        return [
          { x: sourceX, y: sourceY },
          { x: targetX, y: targetY }
        ];
      } else {
        // 4-segment path: down, across, down to target handle
        // This ensures the line always connects to the actual target handle position
        const midY = sourceY + (targetY - sourceY) / 2;
        return [
          { x: sourceX, y: sourceY },
          { x: sourceX, y: midY },
          { x: targetX, y: midY },
          { x: targetX, y: targetY }
        ];
      }
    }
    
    // Determine if the output handle is in the upper or lower half of the node
    // This works for all node types, current and future
    const sourceNode = getNodes().find(n => n.id === source);
    let shouldRouteAbove = false;
    
    if (sourceNode) {
      const nodeY = sourceNode.position.y;
      const nodeHeight = sourceNode.height || 80; // Use actual node height if available
      const nodeCenterY = nodeY + nodeHeight / 2;
      
      // If the source handle is above the node's center, route above
      // If it's below the center, route below
      shouldRouteAbove = sourceY < nodeCenterY;
    }
    
    // Check if this is a backward flow edge (target is to the left of source)
    const isBackward = targetX < sourceX;
    
    // Check if target is significantly below source (sub-node pattern)
    const isSignificantlyBelow = targetY > sourceY + 80; // 80px threshold
    
    // For forward flow OR downward flow (sub-nodes), use simple direct path
    if (!isBackward || isSignificantlyBelow) {
      // Simple direct path for forward connections and downward sub-node connections
      return [
        { x: sourceX, y: sourceY },
        { x: sourceX + EXIT_DISTANCE / 2, y: sourceY },
        { x: targetX - EXIT_DISTANCE / 2, y: targetY },
        { x: targetX, y: targetY }
      ];
    }
    
    // For backward flow, use rectangular routing
    const sourceExit = getExitWaypoint(
      { x: sourceX, y: sourceY },
      sourcePosition || Position.Right,
      EXIT_DISTANCE
    );
    
    const targetEntry = getExitWaypoint(
      { x: targetX, y: targetY },
      targetPosition || Position.Left,
      EXIT_DISTANCE
    );
    
    // Use rectangular routing for backward flow
    const midPath = createRectangularPath(sourceExit, targetEntry, obstacles, shouldRouteAbove);
    
    // Combine: source handle → exit → pathfound route → entry → target handle
    return [
      { x: sourceX, y: sourceY },
      ...midPath,
      { x: targetX, y: targetY }
    ];
  }, [sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, obstacles, actualSourceHandle, id, source, getNodes]);
  
  // ✅ FIX: Detect backward flow (target is to the left of source)
  const isBackwardFlow = useMemo(() => {
    return targetX < sourceX;
  }, [sourceX, targetX]);
  
  const pathData = useMemo(() => {
    const path = createSmoothPath(pathPoints);
    return path;
  }, [pathPoints, isBackwardFlow]);
  
  // Calculate true midpoint of the path for arrow placement
  const { midpoint, angle } = useMemo(() => {
    if (pathPoints.length < 2) {
      return { midpoint: { x: sourceX, y: sourceY }, angle: 0 };
    }
    
    // Calculate total path length
    let totalLength = 0;
    const segments: { start: Point; end: Point; length: number }[] = [];
    
    for (let i = 0; i < pathPoints.length - 1; i++) {
      const start = pathPoints[i];
      const end = pathPoints[i + 1];
      const length = Math.sqrt(
        Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
      );
      segments.push({ start, end, length });
      totalLength += length;
    }
    
    // Find the segment containing the midpoint
    const targetLength = totalLength / 2;
    let accumulatedLength = 0;
    
    for (const segment of segments) {
      if (accumulatedLength + segment.length >= targetLength) {
        const remainingLength = targetLength - accumulatedLength;
        const ratio = remainingLength / segment.length;
        
        const x = segment.start.x + (segment.end.x - segment.start.x) * ratio;
        const y = segment.start.y + (segment.end.y - segment.start.y) * ratio;
        
        // Calculate angle for arrow rotation
        const dx = segment.end.x - segment.start.x;
        const dy = segment.end.y - segment.start.y;
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        
        return { midpoint: { x, y }, angle };
      }
      accumulatedLength += segment.length;
    }
    
    // Fallback to last point
    const lastPoint = pathPoints[pathPoints.length - 1];
    return { midpoint: lastPoint, angle: 0 };
  }, [pathPoints, sourceX, sourceY]);
  
  const labelPosition = useMemo(() => {
    const midIndex = Math.floor(pathPoints.length / 2);
    return {
      x: pathPoints[midIndex].x,
      y: pathPoints[midIndex].y,
    };
  }, [pathPoints]);
  
  const handleMouseMove = (e: React.MouseEvent<SVGPathElement>) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    
    const svg = e.currentTarget.ownerSVGElement;
    if (svg) {
      const point = svg.createSVGPoint();
      point.x = e.clientX;
      point.y = e.clientY;
      const svgPoint = point.matrixTransform(svg.getScreenCTM()?.inverse());
      
      // Calculate if mouse is in the middle 50% of the path
      // Calculate total path length
      let totalLength = 0;
      const segments: { start: Point; end: Point; length: number }[] = [];
      
      for (let i = 0; i < pathPoints.length - 1; i++) {
        const start = pathPoints[i];
        const end = pathPoints[i + 1];
        const length = Math.sqrt(
          Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
        );
        segments.push({ start, end, length });
        totalLength += length;
      }
      
      // Find which segment and position the mouse is at
      let mousePathLength = 0;
      let foundSegment = false;
      
      for (const segment of segments) {
        // Check if mouse is near this segment
        const dx = segment.end.x - segment.start.x;
        const dy = segment.end.y - segment.start.y;
        const segmentLength = segment.length;
        
        if (segmentLength > 0) {
          // Project mouse point onto segment
          const t = Math.max(0, Math.min(1, 
            ((svgPoint.x - segment.start.x) * dx + (svgPoint.y - segment.start.y) * dy) / (segmentLength * segmentLength)
          ));
          
          const projX = segment.start.x + t * dx;
          const projY = segment.start.y + t * dy;
          const distToSegment = Math.sqrt(
            Math.pow(svgPoint.x - projX, 2) + Math.pow(svgPoint.y - projY, 2)
          );
          
          // If mouse is close to this segment (within 20px)
          if (distToSegment < 20) {
            mousePathLength += t * segmentLength;
            foundSegment = true;
            break;
          }
        }
        
        if (!foundSegment) {
          mousePathLength += segmentLength;
        }
      }
      
      // Check if mouse is in middle 50% (between 25% and 75% of path length)
      const pathRatio = mousePathLength / totalLength;
      const isInMiddle50 = pathRatio >= 0.25 && pathRatio <= 0.75;
      
      if (isInMiddle50) {
        setMousePosition({ x: svgPoint.x, y: svgPoint.y });
      } else {
        setMousePosition(null);
      }
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<SVGPathElement>) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHovered(true);
    handleMouseMove(e);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      setMousePosition(null);
    }, 100);
  };
  
  const handleButtonsMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeEdge(id);
  };
  
  const handleInsertNode = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data?.onAdd) {
      data.onAdd(id, { sourceX, sourceY, targetX, targetY });
    }
  };
  
  const buttonX = mousePosition?.x ?? labelPosition.x;
  const buttonY = mousePosition?.y ?? labelPosition.y;

  // Get execution status from data (set by WorkflowEditor)
  const executionStatus = (data?.executionStatus as 'idle' | 'active' | 'traversed') || 'idle';
  const isActive = executionStatus === 'active';
  const isTraversed = executionStatus === 'traversed';

  // Determine edge color based on execution status, anchor type, or default
  const getEdgeColor = () => {
    // Drop target takes priority
    if (isDropTarget) {
      return 'rgb(79, 70, 229)'; // Primary indigo for drop target
    }
    // Execution status takes priority
    if (isActive) {
      return 'var(--accent-info-500)'; // Cyan/blue for active flow
    }
    if (isTraversed) {
      return 'var(--accent-success-500)'; // Green for traversed edges
    }

    // Check if this is connecting TO a parser sub-node's model anchor (targetHandle)
    if (actualTargetHandle === 'model-input') {
      const colors = workflowColors.edge.chatModel;
      return isHovered || isSelected ? colors.active : colors.normal;
    }

    // Check if this is an anchor connection FROM an AI Agent node (sourceHandle)
    if (actualSourceHandle === 'chatModel') {
      const colors = workflowColors.edge.chatModel;
      return isHovered || isSelected ? colors.active : colors.normal;
    }
    if (actualSourceHandle === 'memory') {
      const colors = workflowColors.edge.memory;
      return isHovered || isSelected ? colors.active : colors.normal;
    }
    if (actualSourceHandle === 'tools') {
      const colors = workflowColors.edge.tools;
      return isHovered || isSelected ? colors.active : colors.normal;
    }
    if (actualSourceHandle === 'parser') {
      const colors = workflowColors.edge.parser;
      return isHovered || isSelected ? colors.active : colors.normal;
    }

    // Default color: grey by default, primary (teal) when selected/hovered
    const colors = workflowColors.edge.default;
    return isHovered || isSelected ? colors.active : colors.normal;
  };

  const edgeColor = getEdgeColor();

  return (
    <>
      {/* SVG marker definition for arrow at midpoint */}
      <defs>
        <marker
          id={`arrow-${id}`}
          viewBox="0 0 10 10"
          refX="5"
          refY="5"
          markerWidth="8"
          markerHeight="8"
          orient="auto"
        >
          <path
            d="M 0 0 L 10 5 L 0 10 z"
            fill={edgeColor}
            stroke="none"
          />
        </marker>
      </defs>

      {/* Invisible wider path for easier hovering */}
      <path
        d={pathData}
        fill="none"
        strokeWidth={20}
        stroke="transparent"
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: 'pointer' }}
      />
      
      {/* Animated glow effect for active edges (data flowing) */}
      {isActive && (
        <path
          d={pathData}
          fill="none"
          stroke="var(--accent-info-500)"
          strokeWidth={8}
          strokeOpacity={0.3}
          className="animate-pulse"
          style={{
            filter: 'blur(4px)',
          }}
        />
      )}

      {/* Drop target glow effect (when dragging node over edge) */}
      {isDropTarget && (
        <>
          <path
            d={pathData}
            fill="none"
            stroke="rgb(79, 70, 229)"
            strokeWidth={16}
            strokeOpacity={0.4}
            style={{
              filter: 'blur(8px)',
            }}
          />
          <path
            d={pathData}
            fill="none"
            stroke="rgb(79, 70, 229)"
            strokeWidth={8}
            strokeOpacity={0.6}
            style={{
              filter: 'blur(3px)',
            }}
          />
        </>
      )}

      {/* Visible edge path - clean line */}
      <path
        id={id}
        d={pathData}
        style={{
          ...style,
          stroke: edgeColor,
          strokeWidth: isDropTarget ? 3 : isActive ? 2.5 : isTraversed ? 2 : isHovered || isSelected ? 2 : 1.5,
          fill: 'none',
        }}
        className="react-flow__edge-path transition-all"
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />

      {/* Arrow icon at exact midpoint using EdgeLabelRenderer for proper positioning */}
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${midpoint.x}px, ${midpoint.y}px) rotate(${angle}deg)`,
            pointerEvents: 'none',
          }}
          className="nodrag nopan"
        >
          <ArrowRight 
            size={18} 
            strokeWidth={2.5}
            style={{ color: edgeColor }}
            className="transition-all"
          />
        </div>
      </EdgeLabelRenderer>
      
      <EdgeLabelRenderer>
        {/* Always show + button in center when hovered, n8n style */}
        {isHovered && mousePosition && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${buttonX}px,${buttonY}px)`,
              pointerEvents: 'none',
            }}
            className="nodrag nopan"
          >
            <div 
              className="flex gap-1.5 items-center"
              style={{ pointerEvents: 'auto' }}
              onMouseEnter={handleButtonsMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {/* n8n-style prominent + button */}
              <button
                onClick={handleInsertNode}
                className="w-7 h-7 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center"
                title="Add node"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
              </button>
              {/* Delete button - smaller and secondary */}
              <button
                onClick={handleDelete}
                className="w-5 h-5 rounded-full bg-background border border-border hover:bg-destructive hover:text-destructive-foreground hover:border-destructive shadow-sm transition-all flex items-center justify-center"
                title="Delete connection"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
}
